"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { Plus, Edit, Trash2, Search, X, Check, Loader2 } from 'lucide-react'
import { categoriesService, productsService } from "../../services/firebase"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import toast from "react-hot-toast"

// Simplified Category interface for a flat structure
interface Category {
  id: string
  name: string
  slug: string
  description?: string
  image?: string
  isActive: boolean
  tags?: string[] // Tags for the category itself
  createdAt?: string
  updatedAt?: string
  productCount?: number // Added for display
}

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null)
  const [categoryName, setCategoryName] = useState("")
  const [categorySlug, setCategorySlug] = useState("")
  const [categoryDescription, setCategoryDescription] = useState("")
  const [categoryImage, setCategoryImage] = useState("")
  const [categoryIsActive, setCategoryIsActive] = useState(true)
  const [categoryTags, setCategoryTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    setIsLoading(true)
    try {
      const [fetchedCategories, allProducts] = await Promise.all([
        categoriesService.getCategories(),
        productsService.getProducts()
      ])
      // Compute productCount for each category based on products' categories array
      const processedCategories = fetchedCategories.map(cat => {
        const count = allProducts.filter(
          (product: any) =>
            Array.isArray(product.categories) && product.categories.includes(cat.slug)
        ).length
        return {
          ...cat,
          slug: cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          productCount: count,
        }
      })
      setCategories(processedCategories as any)
    } catch (error) {
      console.error("Error loading categories:", error)
      toast.error("Failed to load categories.")
    } finally {
      setIsLoading(false)
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!categoryName.trim()) {
      newErrors.name = "Category name is required."
    }
    if (!categorySlug.trim()) {
      newErrors.slug = "Category slug is required."
    } else if (!/^[a-z0-9-]+$/.test(categorySlug)) {
      newErrors.slug = "Slug can only contain lowercase letters, numbers, and hyphens."
    } else if (
      categories.some(
        (cat) => cat.slug === categorySlug && cat.id !== currentCategory?.id,
      )
    ) {
      newErrors.slug = "Slug must be unique."
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const openAddModal = () => {
    setCurrentCategory(null)
    setCategoryName("")
    setCategorySlug("")
    setCategoryDescription("")
    setCategoryImage("")
    setCategoryIsActive(true)
    setCategoryTags([])
    setTagInput("")
    setErrors({})
    setIsModalOpen(true)
  }

  const openEditModal = (category: Category) => {
    setCurrentCategory(category)
    setCategoryName(category.name)
    setCategorySlug(category.slug)
    setCategoryDescription(category.description || "")
    setCategoryImage(category.image || "")
    setCategoryIsActive(category.isActive)
    setCategoryTags(category.tags || [])
    setTagInput("")
    setErrors({})
    setIsModalOpen(true)
  }

  const closeModals = () => {
    setIsModalOpen(false)
    setIsConfirmModalOpen(false)
  }

  const handleSaveCategory = async () => {
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    try {
      const categoryData: Omit<Category, "id"> = {
        name: categoryName,
        slug: categorySlug,
        description: categoryDescription,
        image: categoryImage,
        isActive: categoryIsActive,
        tags: categoryTags.filter(tag => tag.trim() !== ""),
        createdAt: currentCategory?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      if (currentCategory) {
        await categoriesService.updateCategory(currentCategory.id, categoryData as any)
        toast.success("Category updated successfully!")
      } else {
        await categoriesService.addCategory(categoryData as any)
        toast.success("Category added successfully!")
      }
      await loadCategories() // Reload to get updated product counts
      closeModals()
    } catch (error) {
      console.error("Error saving category:", error)
      toast.error(`Failed to save category: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteCategory = async (categoryId: string) => {
    setCurrentCategory(categories.find((cat) => cat.id === categoryId) || null)
    setIsConfirmModalOpen(true)
  }

  const confirmDeleteCategory = async () => {
    if (!currentCategory) return

    setIsSaving(true)
    try {
      await categoriesService.deleteCategory(currentCategory.id)
      toast.success("Category deleted successfully!")
      await loadCategories()
      closeModals()
    } catch (error) {
      console.error("Error deleting category:", error)
      toast.error(`Failed to delete category: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsSaving(false)
    }
  }

  const addTag = () => {
    if (tagInput.trim() && !categoryTags.includes(tagInput.trim())) {
      setCategoryTags((prev) => [...prev, tagInput.trim()])
      setTagInput("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setCategoryTags((prev) => prev.filter((tag) => tag !== tagToRemove))
  }

  const filteredCategories = useMemo(() => {
    return categories.filter((category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [categories, searchTerm])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600">Manage your product categories</p>
        </div>
        <button
          onClick={openAddModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Category
        </button>
      </div>

      {/* Search and Stats */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="text-sm text-gray-600">
          Total Categories: <span className="font-semibold text-gray-900">{categories.length}</span>
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    No categories found.
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr key={category.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {category.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.slug}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.image ? (
                        <img src={category.image || "/placeholder.svg"} alt={category.name} className="h-10 w-10 object-cover rounded-md" />
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {category.isActive ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-red-500" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.productCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(category)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        title="Edit Category"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Category"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Category Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {currentCategory ? "Edit Category" : "Add New Category"}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value)
                    setCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))
                    setErrors((prev) => ({ ...prev, name: "", slug: "" }))
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  id="slug"
                  value={categorySlug}
                  onChange={(e) => {
                    setCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))
                    setErrors((prev) => ({ ...prev, slug: "" }))
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  readOnly
                />
                {errors.slug && <p className="mt-1 text-sm text-red-600">{errors.slug}</p>}
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                ></textarea>
              </div>
              <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  id="image"
                  value={categoryImage}
                  onChange={(e) => setCategoryImage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., https://example.com/image.jpg"
                />
                {categoryImage && (
                  <img src={categoryImage || "/placeholder.svg"} alt="Category Preview" className="mt-2 h-24 w-24 object-cover rounded-md" />
                )}
              </div>
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={categoryIsActive}
                    onChange={(e) => setCategoryIsActive(e.target.checked)}
                    className="mr-2 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  Active
                </label>
              </div>
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {categoryTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="flex-shrink-0 ml-1.5 h-3 w-3 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-200 focus:text-blue-500"
                      >
                        <span className="sr-only">Remove tag</span>
                        <X className="h-2 w-2" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex">
                  <input
                    type="text"
                    id="tagInput"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        addTag()
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Add a tag (press Enter)"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-primary-600 text-white rounded-r-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCategory}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {currentCategory ? "Update Category" : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the category "
              <span className="font-semibold">{currentCategory?.name}</span>"? This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteCategory}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCategories
